import { FormFields, FormProps } from '../utilities/Form'
import { WorkflowTemplate } from '../components/organisms/workflow-template/types'
import { IDVStatus } from '../utilities/steps'

declare global {
  interface Window {
    VGSCollect: VGSCollect
  }
}

export type VgsState = {
  ssn: VgsFieldState
  ssnConfirm: VgsFieldState
}

export type VgsFieldState = {
  errorMessages: string[]
  isDirty: boolean
  isEmpty: boolean
  isFocused: boolean
  isTouched: boolean
  isValid: boolean
  last4: string
}

export type VGSCollect = {
  create: (
    vaultId: string,
    env: string,
    onStateChange: (state: VgsState) => void
  ) => void
}

export type Referral = {
  id?: number
  name?: string
  referreeBonus?: any
  referralExpiration?: any
  referralExpirationInDays?: any
  targetNumDeliveries?: number
}

export type ISO8601String = string

export enum CheckrStatus {
  Waiting = 'WAITING',
  Delayed = 'DELAYED',
  NeedsAttention = 'NEEDS_ATTENTION',
  Clear = 'CLEAR',
}
export enum ThinFileStatus {
  Pending = 'PENDING',
  Clear = 'CLEAR',
}

export enum ShippingStatus {
  NotCreated = 'NOT_CREATED',
  Created = 'CREATED',
  Shipped = 'SHIPPED',
  Delivered = 'DELIVERED',
  Error = 'ERROR',
}

export type ApplicationStatus = {
  step: number
  onboardingProgress: any
  bgcheckStatus: CheckrStatus
  shippingStatus: ShippingStatus
  shippingArrivalDate: ISO8601String
  shippingUrl: string
  shippingAddress: string
  verificationStatus: any
  thinFileStatus?: ThinFileStatus
  instantDashingV2Status: boolean
}

export type OnboardingProgress = {
  backgroundCheckComplete: boolean
  hasCompletedFirstDelivery: boolean
  firstDeliveryComplete: boolean
  isDasherInDxSafetyCourseSlides: boolean
  paymentAccountSetupComplete: boolean
  isDasherInDxSafetyCourse: boolean
  redCardActivationComplete: boolean
  hasCompletedSafetyCourse: boolean
  fastPaySetupComplete: boolean
  hasScheduledDash: boolean
}

export type Applicant = {
  email: string
  firstName: string
  uniqueLink: string
  backgroundCheckCompletionUrl?: string
}

export type GoogleAddress = {
  city: string
  googlePlaceId: string
  lat: number
  lng: number
  printableAddress: string
  shortname: string
  state: string
  street: string
  zipCode: string
}

export type StripeCredentials = {
  publicKey: string
  countryShortname: string
  currencyCode: string
}

export type StripeFileResponse = {
  id: string
  object: string
  created: number
  filename: string
  purpose: string
  size: number
  type: string
  url: string
}

export type Photo = {
  file: File
  blob: Blob
}

export enum Experiment {
  CONTROL = 'control',
  TREATMENT = 'treatment',
}

export type WebviewExperiment = {
  [key: string]: Experiment
}

export enum AlreadyStartedEnum {
  Default = 'default',
  ExistingCredentials = 'existingCredentials',
  LoggedIn = 'loggedIn',
  Closed = 'closed',
}

export type AlreadyStartedSigningUpModalProps = {
  isShowing?: boolean
  email?: string
  phoneNumber?: string
  type?: AlreadyStartedEnum
}

/**
 * DeliveryIssue is an object representing the details of a given delivery
 * issue that might be of interest to either DoorDash or dashers.
 *
 * **issueType:** String(s) as enums representing what kind of issue it is.
 *
 * **priority:** Number representing how important it is to inform the
 * dasher of this particular issue. Priority 1 is highest.
 *
 * **issueData:** Primarily used for Extremely Late but can potentially include
 * meta data for other delivery issues in the future
 */
export type DeliveryIssue = {
  createdAt: string
  deliveryCreatedAt: string
  id: string
  issueType: DeliveryIssueTypes
  priority: number
  status: DeliveryIssueStatus
  steps: DeliveryIssueStep[]
  storeName: string
  issueData: DeliveryIssueData
}

/**
 * Delivery Issue Data for extremely late
 * a null on either pickup or dropoff signifies that
 * the dasher was not extremely late
 */
export type DeliveryIssueData = {
  pickUp?: ExtremelyLateData | null
  dropOff?: ExtremelyLateData | null
}

/**
 * Object containing data on how late
 */
export type ExtremelyLateData = {
  minutesLate: number
  scheduledTime: string
  actualTime: string
}
/**
 * The shape of what we parse from the Delivery Issues List response to the
 * redux store so we don't store more than is needed to render the list.
 */
export type DeliveryIssueListItem = {
  deliveryCreatedAt: string
  id: string
  status: DeliveryIssueStatus
  storeName: string
  priority: number
  issueType: DeliveryIssueTypes
  onClick?: () => void
}

/**
 * Different types of possible delivery issues
 */
export enum DeliveryIssueTypes {
  NeverDelivered = 'never_delivered',
  ExtremelyLate = 'extremely_late',
}

/**
 * DeliveryIssueStatus: Strings as enums to represent the issue's state. Read
 * and unread are based on whether the dasher views the issue. All other
 * statuses become relevant after the dasher submits a dispute for the issue.
 * It is assumed that all dispute-related statuses imply the issue is read.
 */
export enum DeliveryIssueStatus {
  Unread = 'unread',
  Read = 'read',
  NeedsReview = 'needs_review',
  InReview = 'in_review',
  DisputeAccepted = 'dispute_accepted',
  DisputeRejected = 'dispute_rejected',
}

/**
 * DeliveryIssueStep is an object representing information about an action that
 * dashers should perform as part of the delivery process.
 */
export type DeliveryIssueStep = {
  type: DeliveryIssueStepTypes
  result: DeliveryIssueStepResults
}

/**
 * DeliveryIssueStepTypes: Strings as enums that represent a specific UI
 * element or component describing the step to help dashers understand what
 * is expected of them.
 */
export enum DeliveryIssueStepTypes {
  DxEnteredGeoFence = 'dx_entered_geofence',
  DxContactedCx = 'dx_contacted_cx',
  DxWaited = 'dx_waited',
  DxLeftPhoto = 'dx_left_photo',
}

/**
 * DeliveryIssueStepResults: Strings as enums that represent whether the
 * dasher successfully performed the actions for a given step. Used to in UI
 * for dasher feedback.
 */
export enum DeliveryIssueStepResults {
  Success = 'success',
  Failure = 'failure',
}

export type DeliveryIssueDispute = {
  isSubmitting: boolean
  isSubmitted: boolean
  error?: Error
}

/**
 * PolicyViolationPages: enums for possible pages as dasher navigates through
 * delivery issue screens
 */
export enum PolicyViolationPages {
  Takeover = 'takeover',
  ViolationList = 'violation_list',
  ViolationDetail = 'violation_detail',
  LearnMore = 'learn_more',
  Dispute = 'dispute',
}

/**
 * Fast Pay response
 */
export type FastPay = {
  country?: string
  currency?: string
  id?: number
  token?: string
  paymentAccountId?: number
  stripeCardId?: string
  last4?: string
  brand?: string
  isDefault?: boolean
  expMonth?: number
  expYear?: number
  createdAt?: Date
  updatedAt?: Date
}

export type VehiclePatchData = {
  make: string
  model: string
  color: string
}

export type GetSales = {
  phone?: string
  timestamp?: string
  isSubmitting: boolean
  wait?: number
}

export type Validator = (
  value: string,
  props?: FormProps,
  fields?: FormFields
) => string

export type ValidatorList = {
  [key: string]: Validator | (() => void)
}

export enum ArchiveAccountState {
  NoStatus = 'no_status',
  Pending = 'pending',
  Ready = 'ready',
}

export enum PrivacyMode {
  RTI = 'rti',
  RTF = 'rtf',
}

export enum RTIPrivacyFlow {
  Request = 'rti-request',
  PreparingArchive = 'preparing-archive',
}

export enum RTFPrivacyFlow {
  Request = 'rtf-request',
  Confirm = 'confirm',
  Exit = 'exit',
}

export enum Channel {
  Sms = 'sms',
  Email = 'email',
}

export type VehicleDashStatusForVehicle = {
  vehicleType: number
  busynessStatus: BusynessStatus
}

export enum BusynessStatus {
  Busy = 'busy',
  Normal = 'normal',
  VeryBusy = 'very_busy',
  NotBusy = 'not_busy',
}

export type SupportedCountryCode = 'US' | 'PR' | 'CA' | 'AU' | 'JP' | 'DE'

export type OnBoardingWorkflow = {
  currentStep: string
  currentStepStatus: WorkflowStepStatus
  error: string
  isLoading: boolean
  stepsComplete: boolean
  template: WorkflowTemplate
}

export type WorkflowStepStatus =
  | 'STEP_STATUS_UNSPECIFIED'
  | 'STEP_STATUS_IN_PROGRESS'
  | 'STEP_STATUS_NOT_STARTED'
  | 'STEP_STATUS_COMPLETED'
  | 'STEP_STATUS_FAILED'

export type EmploymentType = 'INDIVIDUAL_CONTRACTOR' | 'W2'

export type IDVStatusResponse = {
  value?: IDVStatus
  isSubmitting?: boolean
  isSubmitted?: boolean
}
